import * as api from "../api/client";
import { ConfidenceBadge } from "../components/ConfidenceBadge";
import { MetricsDashboard } from "../components/MetricsDashboard";
import { ProductionFailures } from "../components/ProductionFailures";
import { SortableTh } from "../components/SortableTh";
import { useApi } from "../hooks/useApi";
import { useSortableRows } from "../hooks/useSortableRows";
import type { ModelMetrics, PromptMetrics } from "../types/api";

type PromptSortKey = "name" | "type" | "status" | "usageCount" | "satisfactionRate" | "alpha";
const PROMPT_ACCESSORS: Record<PromptSortKey, (p: PromptMetrics) => string | number> = {
  name: (p) => p.name,
  type: (p) => p.type,
  status: (p) => p.status,
  usageCount: (p) => p.usageCount,
  satisfactionRate: (p) => p.satisfactionRate,
  alpha: (p) => p.alpha,
};

type ModelSortKey =
  | "displayName"
  | "provider"
  | "status"
  | "usageCount"
  | "satisfactionRate"
  | "avgLatencyMs"
  | "avgTtftMs"
  | "totalEstimatedCostUsd";
const MODEL_ACCESSORS: Record<ModelSortKey, (m: ModelMetrics) => string | number> = {
  displayName: (m) => m.displayName,
  provider: (m) => m.provider,
  status: (m) => m.status,
  usageCount: (m) => m.usageCount,
  satisfactionRate: (m) => m.satisfactionRate,
  avgLatencyMs: (m) => m.avgLatencyMs,
  avgTtftMs: (m) => m.avgTtftMs,
  totalEstimatedCostUsd: (m) => m.totalEstimatedCostUsd,
};

export function MetricsPage() {
  const overview = useApi(() => api.getOverviewMetrics(), []);
  const promptMetrics = useApi(() => api.getPromptMetrics(), []);
  const modelMetrics = useApi(() => api.getModelMetrics(), []);
  const pluginFailuresSummary = useApi(() => api.getPluginFailuresSummary(), []);
  const pluginFailuresRecent = useApi(() => api.getRecentPluginFailures(10), []);
  const clientFailuresSummary = useApi(() => api.getClientFailuresSummary(), []);
  const clientFailuresRecent = useApi(() => api.getRecentClientFailures(10), []);

  // Default order: highest-usage prompt/model first — the thing worth looking
  // at without clicking anything (PK 2026-07-25 directive).
  const promptSort = useSortableRows(promptMetrics.data, PROMPT_ACCESSORS, {
    key: "usageCount",
    direction: "desc",
  });
  const modelSort = useSortableRows(modelMetrics.data, MODEL_ACCESSORS, {
    key: "usageCount",
    direction: "desc",
  });

  if (overview.loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500 text-sm">Loading metrics...</div>
      </div>
    );
  }

  if (overview.error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
        <div className="text-red-800 text-sm font-medium">Error: {overview.error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-1">Metrics Overview</h2>
        <p className="text-sm text-slate-600">Real-time performance metrics across all services</p>
      </div>

      {overview.data && <MetricsDashboard metrics={overview.data} />}

      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Production Failures</h3>
          <p className="text-sm text-slate-600">
            Plugin + webpage crash channels (V2M-DEV-010) — previously write-only, real-time ntfy
            push only.
          </p>
        </div>
        <ProductionFailures
          pluginSummary={pluginFailuresSummary.data}
          pluginRecent={pluginFailuresRecent.data?.reports ?? []}
          clientSummary={clientFailuresSummary.data}
          clientRecent={clientFailuresRecent.data?.reports ?? []}
        />
      </div>

      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Per-Prompt Metrics</h3>
          <p className="text-sm text-slate-600">Performance breakdown by prompt configuration</p>
        </div>

        {promptMetrics.loading && (
          <div className="text-slate-500 text-sm">Loading prompt metrics...</div>
        )}

        {promptMetrics.data && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <SortableTh
                      label="Name"
                      sortKey="name"
                      sort={promptSort.sort}
                      onSort={promptSort.toggleSort}
                      sticky
                    />
                    <SortableTh
                      label="Type"
                      sortKey="type"
                      sort={promptSort.sort}
                      onSort={promptSort.toggleSort}
                    />
                    <SortableTh
                      label="Status"
                      sortKey="status"
                      sort={promptSort.sort}
                      onSort={promptSort.toggleSort}
                    />
                    <SortableTh
                      label="Usage"
                      sortKey="usageCount"
                      sort={promptSort.sort}
                      onSort={promptSort.toggleSort}
                    />
                    <SortableTh
                      label="Satisfaction"
                      sortKey="satisfactionRate"
                      sort={promptSort.sort}
                      onSort={promptSort.toggleSort}
                    />
                    <SortableTh
                      label="Alpha/Beta"
                      sortKey="alpha"
                      sort={promptSort.sort}
                      onSort={promptSort.toggleSort}
                    />
                    <th className="text-left px-6 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Confidence
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {promptSort.sorted.map((p) => (
                    <tr key={p.id} className="group hover:bg-slate-50 transition-colors">
                      <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 px-6 py-4 text-sm font-medium text-slate-900">
                        {p.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{p.type}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{p.status}</td>
                      <td className="px-6 py-4 text-sm text-slate-900">{p.usageCount}</td>
                      <td className="px-6 py-4 text-sm text-slate-900">
                        {(p.satisfactionRate * 100).toFixed(1)}%
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {p.alpha}/{p.beta}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <ConfidenceBadge badge={p.confidenceBadge} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Per-Model Metrics</h3>
          <p className="text-sm text-slate-600">Performance breakdown by AI model</p>
        </div>

        {modelMetrics.loading && (
          <div className="text-slate-500 text-sm">Loading model metrics...</div>
        )}

        {modelMetrics.data && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <SortableTh
                      label="Name"
                      sortKey="displayName"
                      sort={modelSort.sort}
                      onSort={modelSort.toggleSort}
                      sticky
                    />
                    <SortableTh
                      label="Provider"
                      sortKey="provider"
                      sort={modelSort.sort}
                      onSort={modelSort.toggleSort}
                    />
                    <SortableTh
                      label="Status"
                      sortKey="status"
                      sort={modelSort.sort}
                      onSort={modelSort.toggleSort}
                    />
                    <SortableTh
                      label="Usage"
                      sortKey="usageCount"
                      sort={modelSort.sort}
                      onSort={modelSort.toggleSort}
                    />
                    <SortableTh
                      label="Satisfaction"
                      sortKey="satisfactionRate"
                      sort={modelSort.sort}
                      onSort={modelSort.toggleSort}
                    />
                    <SortableTh
                      label="Avg Latency"
                      sortKey="avgLatencyMs"
                      sort={modelSort.sort}
                      onSort={modelSort.toggleSort}
                    />
                    <SortableTh
                      label="Avg TTFT"
                      sortKey="avgTtftMs"
                      sort={modelSort.sort}
                      onSort={modelSort.toggleSort}
                    />
                    <SortableTh
                      label="Cost (USD)"
                      sortKey="totalEstimatedCostUsd"
                      sort={modelSort.sort}
                      onSort={modelSort.toggleSort}
                    />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {modelSort.sorted.map((m, i) => (
                    // Composite key, not bare `m.id` — see ModelsPage.tsx for the full
                    // explanation: two real dev-DB model docs share the same `id` field
                    // (stale renamed-model artifact), which collides as a React key.
                    <tr key={`${m.id}-${i}`} className="group hover:bg-slate-50 transition-colors">
                      <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 px-6 py-4 text-sm font-medium text-slate-900">
                        {m.displayName}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{m.provider}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{m.status}</td>
                      <td className="px-6 py-4 text-sm text-slate-900">{m.usageCount}</td>
                      <td className="px-6 py-4 text-sm text-slate-900">
                        {(m.satisfactionRate * 100).toFixed(1)}%
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-900">
                        {m.avgLatencyMs.toFixed(0)}ms
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-900">
                        {m.avgTtftMs.toFixed(0)}ms
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-900">
                        ${m.totalEstimatedCostUsd.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
